import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

const jobRoles = [
  {
    title: 'REVENUE CYCLE MANAGEMENT SPECIALIST',
    location: 'USA (Remote)',
    description: 'Expert in healthcare RCM, medical billing, and claims processing with 3+ years experience',
    type: 'Full-Time',
    category: 'RCM Operations',
  },
  {
    title: 'MEDICAL BILLING SPECIALIST',
    location: 'India',
    description: 'Handle medical coding, billing, and insurance verification for US healthcare providers',
    type: 'Full-Time',
    category: 'Medical Billing',
  },
  {
    title: 'HEALTHCARE CLAIMS ANALYST',
    location: 'India',
    description: 'Analyze and process healthcare claims, denials management, and payment posting',
    type: 'Full-Time',
    category: 'Claims Processing',
  },
  {
    title: 'MEDICAL CODING SPECIALIST (CPC/CCS)',
    location: 'India/USA',
    description: 'ICD-10, CPT, HCPCS coding expertise for multi-specialty healthcare practices',
    type: 'Full-Time',
    category: 'Medical Coding',
  },
  {
    title: 'DENIAL MANAGEMENT SPECIALIST',
    location: 'India',
    description: 'Expert in denial analysis, appeals, and revenue recovery strategies',
    type: 'Full-Time',
    category: 'Denial Management',
  },
  {
    title: 'ACCOUNTS RECEIVABLE (AR) FOLLOW-UP SPECIALIST',
    location: 'India',
    description: 'AR calling, payment follow-up, and insurance correspondence for US healthcare',
    type: 'Full-Time',
    category: 'AR Management',
  },
  {
    title: 'CREDENTIALING SPECIALIST',
    location: 'India',
    description: 'Provider enrollment, credentialing, and payer contracting specialist',
    type: 'Full-Time',
    category: 'Credentialing',
  },
  {
    title: 'RCM OPERATIONS MANAGER',
    location: 'India',
    description: 'Lead RCM operations team, KPI management, and process optimization',
    type: 'Full-Time',
    category: 'Management',
  },
  {
    title: 'AI HEALTHCARE INTERN',
    location: 'India',
    description: 'Work on AI-powered RCM automation and healthcare technology projects',
    type: 'Internship',
    category: 'AI & Technology',
  },
  {
    title: 'PROJECT MANAGER - HEALTHCARE RCM',
    location: 'India',
    description: 'Manage healthcare RCM projects, client coordination, and team leadership',
    type: 'Full-Time',
    category: 'Management',
  },
  {
    title: 'BUSINESS DEVELOPMENT MANAGER',
    location: 'USA',
    description: 'Drive growth in healthcare RCM services, client acquisition, and partnerships',
    type: 'Full-Time',
    category: 'Business Development',
  },
  {
    title: 'QUALITY ANALYST - MEDICAL BILLING',
    location: 'India',
    description: 'Quality audits, HIPAA compliance, and billing accuracy monitoring',
    type: 'Full-Time',
    category: 'Quality Assurance',
  },
  {
    title: 'PATIENT ACCESS REPRESENTATIVE',
    location: 'India',
    description: 'Insurance verification, prior authorization, and patient intake specialist',
    type: 'Full-Time',
    category: 'Patient Access',
  },
  {
    title: 'HEALTHCARE DATA ANALYST',
    location: 'India/USA',
    description: 'RCM analytics, reporting, and data-driven insights for healthcare providers',
    type: 'Full-Time',
    category: 'Data Analytics',
  },
];

const cardStyle = {
  backgroundColor: '#fff',
  border: '1px solid #eee',
  borderRadius: '16px',
  padding: '28px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  transition: 'all 0.3s ease',
  flex: '1 1 calc(33.333% - 20px)',
  minWidth: '280px',
  margin: '10px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
};

const Career = () => {
  return (
    <>
      <Helmet>
        {/* Primary Meta Tags - SEO OPTIMIZED */}
        <title>Healthcare RCM Careers | Medical Billing Jobs | MDeRCM</title>
        <meta 
          name="description" 
          content="Join MDeRCM's team. Healthcare RCM careers, medical billing and RCM analyst roles. Build your career in healthcare technology." 
        />
        
        {/* Keywords */}
        <meta 
          name="keywords" 
          content="healthcare RCM jobs, medical billing careers, revenue cycle management jobs, healthcare billing specialist, medical coding jobs, RCM analyst positions, healthcare jobs USA, medical billing jobs India" 
        />

        {/* Open Graph */}
        <meta property="og:title" content="Healthcare RCM Careers at MDeRCM | Medical Billing Jobs" />
        <meta property="og:description" content="Join the leading healthcare revenue cycle management company. Exciting career opportunities in RCM, medical billing, and healthcare technology." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mdercm.com/careers" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://mdercm.com/careers" />

        {/* Structured Data - JobPosting */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "JobPosting",
            "title": "Healthcare Revenue Cycle Management Careers",
            "description": "Multiple positions available in healthcare RCM, medical billing, and coding",
            "hiringOrganization": {
              "@type": "Organization",
              "name": "MDeRCM",
              "sameAs": "https://mdercm.com"
            },
            "jobLocation": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressCountry": ["US", "IN"]
              }
            },
            "employmentType": ["FULL_TIME", "INTERN"],
            "industry": "Healthcare Revenue Cycle Management"
          })}
        </script>
      </Helmet>

      <div style={{ 
        fontFamily: 'Poppins, sans-serif', 
        padding: '60px 20px', 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        minHeight: '100vh',
      }}>
        {/* Header Section - SEO OPTIMIZED */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', marginBottom: '50px' }}
        >
          <h1 style={{ 
            fontSize: '48px', 
            fontWeight: '700', 
            marginBottom: '20px',
            background: 'linear-gradient(135deg, #00203F 0%, #F2557A 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Healthcare Revenue Cycle Management Careers
          </h1>
          
          <p style={{ 
            fontSize: '18px', 
            color: '#555', 
            maxWidth: '800px', 
            margin: '0 auto 20px',
            lineHeight: '1.6'
          }}>
            Join MDeRCM, one of the <strong>best revenue cycle management companies</strong> in the USA. 
            We're hiring talented professionals for <strong>medical billing outsourcing services</strong>, 
            RCM operations, and healthcare technology positions.
          </p>

          <p style={{
            fontSize: '16px',
            color: '#666',
            maxWidth: '700px',
            margin: '0 auto',
            fontStyle: 'italic',
          }}>
            Be part of transforming <strong>healthcare revenue cycle management services</strong> with AI-powered automation and expert consulting.
          </p>
        </motion.div>

        {/* Category Filter Info */}
        <div style={{
          textAlign: 'center',
          marginBottom: '40px',
        }}>
          <p style={{
            fontSize: '16px',
            color: '#0077b6',
            fontWeight: '600',
          }}>
            🎯 {jobRoles.length} Open Positions | Full-Time & Internship Opportunities
          </p>
        </div>

        {/* Jobs Grid */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          justifyContent: 'center', 
          maxWidth: '1400px', 
          margin: '0 auto',
          gap: '0',
        }}>
          {jobRoles.map((role, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              style={cardStyle}
              whileHover={{
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
              }}
            >
              {/* Category Badge */}
              <div style={{
                display: 'inline-block',
                padding: '6px 14px',
                backgroundColor: role.category === 'RCM Operations' ? '#0077b6' : 
                                role.category === 'Medical Billing' ? '#00b894' :
                                role.category === 'Management' ? '#6c5ce7' :
                                role.category === 'AI & Technology' ? '#e91e63' : '#00cec9',
                color: '#fff',
                borderRadius: '20px',
                fontSize: '11px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '15px',
              }}>
                {role.category}
              </div>

              {/* Job Title */}
              <h3 style={{ 
                color: '#00203F', 
                fontWeight: '700', 
                fontSize: '20px', 
                marginBottom: '12px',
                lineHeight: '1.3',
              }}>
                {role.title}
              </h3>

              {/* Location & Type */}
              <div style={{
                display: 'flex',
                gap: '15px',
                marginBottom: '15px',
                flexWrap: 'wrap',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px',
                  color: '#555',
                  fontWeight: '500',
                }}>
                  <span style={{ marginRight: '6px' }}>📍</span>
                  {role.location}
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '14px',
                  color: '#555',
                  fontWeight: '500',
                }}>
                  <span style={{ marginRight: '6px' }}>💼</span>
                  {role.type}
                </div>
              </div>

              {/* Description */}
              <p style={{ 
                fontSize: '14px', 
                color: '#666', 
                lineHeight: '1.6',
                marginBottom: '20px',
                flexGrow: 1,
              }}>
                {role.description}
              </p>

              {/* Divider */}
              <div style={{
                height: '1px',
                backgroundColor: '#e0e0e0',
                marginBottom: '20px',
              }} />

              {/* Contact Info */}
              <div>
                <p style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#333',
                  marginBottom: '8px',
                }}>
                  📧 Apply Now:
                </p>
                <a 
                  href="mailto:hr@mdercm.com?subject=Application for Healthcare RCM Position"
                  style={{ 
                    color: '#F2557A',
                    fontSize: '15px',
                    fontWeight: '700',
                    textDecoration: 'none',
                    display: 'inline-block',
                    transition: 'color 0.3s ease',
                  }}
                  onMouseEnter={(e) => e.target.style.color = '#d3426b'}
                  onMouseLeave={(e) => e.target.style.color = '#F2557A'}
                >
                  hr@mdercm.com
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA Section - SEO OPTIMIZED */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            marginTop: '80px',
            padding: '50px 30px',
            backgroundColor: '#00203F',
            borderRadius: '20px',
            maxWidth: '900px',
            margin: '80px auto 0',
            textAlign: 'center',
            color: '#ffffff',
          }}
        >
          <h2 style={{
            fontSize: '32px',
            fontWeight: '700',
            marginBottom: '20px',
          }}>
            Why Join MDeRCM?
          </h2>
          <p style={{
            fontSize: '17px',
            lineHeight: '1.8',
            marginBottom: '30px',
            maxWidth: '750px',
            margin: '0 auto 30px',
          }}>
            As a leader in <strong style={{ color: '#00cec9' }}>healthcare revenue cycle management services</strong>, 
            we offer competitive compensation, career growth opportunities, and the chance to work with cutting-edge 
            AI technology in healthcare. Join our team of experts in <strong style={{ color: '#00cec9' }}>medical billing outsourcing services</strong> and make a real impact in healthcare.
          </p>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '30px',
            flexWrap: 'wrap',
            marginTop: '40px',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#00cec9' }}>200+</div>
              <div style={{ fontSize: '14px', color: '#ccc' }}>Healthcare Practices</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#00cec9' }}>200+</div>
              <div style={{ fontSize: '14px', color: '#ccc' }}>Team Members</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: '700', color: '#00cec9' }}>98%</div>
              <div style={{ fontSize: '14px', color: '#ccc' }}>Client Satisfaction</div>
            </div>
          </div>

          <motion.a
            href="mailto:hr@mdercm.com?subject=Career Inquiry - Healthcare RCM Position"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              display: 'inline-block',
              marginTop: '40px',
              padding: '16px 40px',
              backgroundColor: '#F26522',
              color: '#fff',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '700',
              textDecoration: 'none',
              boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)',
            }}
          >
            Email Your Resume Today
          </motion.a>
        </motion.div>

        {/* Keywords Footer */}
        <div style={{
          marginTop: '60px',
          textAlign: 'center',
          padding: '30px',
          backgroundColor: '#fff',
          borderRadius: '15px',
          maxWidth: '1000px',
          margin: '60px auto 0',
        }}>
          <p style={{
            fontSize: '15px',
            lineHeight: '1.8',
            color: '#666',
            fontStyle: 'italic',
          }}>
            We're hiring for <strong>hospital revenue cycle management</strong>, <strong>medical billing and coding outsourcing</strong>, 
            <strong> revenue cycle management consulting services</strong>, and more positions across our <strong>revenue cycle management services USA</strong> operations. 
            Join one of the <strong>best revenue cycle management companies</strong> and advance your career in healthcare.
          </p>
        </div>
      </div>
    </>
  );
};

export default Career;